/**
 * @module @app/shared/files/parser/csv/observable/error_messages
 * @description Error messages that can be presented to the user by the observable parser.
 *
 * @note
 * This file is dependent of the localization implementation.
 */
import type { ObservableParsingError, ObservableParsingErrorMessageMap } from ".";
import { OBSERVABLE_PARSING_ERROR } from ".";
import t from "../../../../i18n";
import { FILE_PARSING_ERROR } from "../../common/file";

/**
 * @description Error messages that can be presented to the user by the observable parser.
 */
const ObservableParsingErrorMessages: ObservableParsingErrorMessageMap = {
  [FILE_PARSING_ERROR.FILE_EMPTY]: () =>
    t("setup.diagnosis.observable.import_observables.error.empty_file"),
  [FILE_PARSING_ERROR.FILE_SIZE_LIMIT_EXCEEDED]: () =>
    t("setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded"),
  [FILE_PARSING_ERROR.FILE_INVALID_TYPE]: () =>
    t("setup.diagnosis.observable.import_observables.error.invalid_format"),
  [OBSERVABLE_PARSING_ERROR.FILE_INVALID_CONTENT]: () =>
    t("setup.diagnosis.observable.import_observables.error.invalid_content"),
  [OBSERVABLE_PARSING_ERROR.DATA_DUPLICATED]: errorData =>
    t("setup.diagnosis.observable.import_observables.error.data_duplicated", errorData.value || ""),
  [OBSERVABLE_PARSING_ERROR.DATA_QUANTITY_ID_MISSING]: () =>
    t("setup.diagnosis.observable.import_observables.error.data_quantity_id_missing"),
  [OBSERVABLE_PARSING_ERROR.DATA_QUANTITY_ID_NOT_ALLOWED]: errorData =>
    t(
      "setup.diagnosis.observable.import_observables.error.data_quantity_id_not_allowed",
      errorData.value || "",
    ),
  [OBSERVABLE_PARSING_ERROR.DATA_ACTIVATED_INVALID]: errorData =>
    t(
      "setup.diagnosis.observable.import_observables.error.data_activated_invalid",
      errorData.hint || "",
    ),
  [OBSERVABLE_PARSING_ERROR.DATA_SIGMA_INVALID]: errorData =>
    t(
      "setup.diagnosis.observable.import_observables.error.data_sigma_invalid",
      errorData.hint || "",
    ),
  [OBSERVABLE_PARSING_ERROR.DATA_SIGMA_POSITIVE]: errorData =>
    t(
      "setup.diagnosis.observable.import_observables.error.data_sigma_invalid",
      errorData.hint || "",
    ),
  [OBSERVABLE_PARSING_ERROR.DATA_SIGMA_ACTIVATED_REQUIRED]: errorData =>
    t(
      "setup.diagnosis.observable.import_observables.error.data_sigma_activated_required",
      errorData.hint || "",
    ),
  [OBSERVABLE_PARSING_ERROR.HAS_TIME_FUNCTION_DEPENDENCY]: () =>
    t("setup.simulation.input_model.error.has_time_function_dependency"),
};

/**
 * @description Get error message from error kind.
 * @param error ObservableParsingError
 * @returns Translated error message.
 */
export const getObservableParsingErrorMessage = (error: ObservableParsingError) => {
  const errorMessage = ObservableParsingErrorMessages[error.kind];
  return errorMessage ? errorMessage(error.data) : "Unknown error";
};
